/* eslint-disable max-lines */
import { LoadingButton } from '@mui/lab';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ComputerIcon from '@mui/icons-material/Computer';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InboxIcon from '@mui/icons-material/Inbox';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keyframes, useTheme } from '@mui/material/styles';
import { getOrderShipmentsApi } from 'api/order';
import { splitShipmentItemsApi, updateShipmentItemsApi } from 'api/shipment';
import { filter, isEmpty, map } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useSelector } from 'react-redux';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useSnackbar } from 'notistack';
import useStrictDroppable from 'common/hooks/useStrictDroppable';
import ItemRow from './ManageShipmentItemsItemRow';
import TargetShipmentAccordion from './ManageShipmentItemsTargetAccordion';
import ManageShipmentItemsFilterStatusBar from './ManageShipmentItemsFilterStatusBar';
import ManageShipmentItemsFilterButton, { emptyFilter } from './ManageShipmentItemsFilterButton';
import ManageShipmentItemsContext from './ManageShipmentItemsContext';

const DROPPABLE_CURRENT = 'current';

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scanAnim = keyframes`
  0%, 100% { transform: translateX(-6px) rotate(-10deg); }
  50% { transform: translateX(6px) rotate(10deg); }
`;

const PAGE_SIZE = 15;
const MAX_ITEMS_PER_SHIPMENT = Number(process.env.REACT_APP_BATCH_SHIPMENT_DEFAULT_SELECT) || 50;

// ─── Pure helpers ──────────────────────────────────────────────────────────────

const resolveShipment = ({ shipmentId, otherShipments, searchResults }) =>
  otherShipments.find((s) => s.shipmentId === shipmentId) ||
  searchResults?.find((s) => s.shipmentId === shipmentId) ||
  null;

const removeItemFromDrops = ({ droppedItems, targetId, referenceId }) => {
  const move = droppedItems[targetId];
  if (!move) return droppedItems;
  const remaining = move.items.filter((i) => i.referenceId !== referenceId);
  const updated = { ...droppedItems };
  if (remaining.length === 0) {
    delete updated[targetId];
  } else {
    updated[targetId] = { ...move, items: remaining };
  }
  return updated;
};

const removeAllFromDrops = ({ droppedItems, targetId }) => {
  const updated = { ...droppedItems };
  delete updated[targetId];
  return updated;
};

const resolveOriginalItems = ({ dropItems, shipmentItems }) =>
  dropItems
    .map((i) => (shipmentItems || []).find((s) => s.lineItemId === i.referenceId))
    .filter(Boolean);

// ───────────────────────────────────────────────────────────────────────────────

const ManageShipmentItemsShipmentSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {[0, 1, 2].map((i) => (
      <Paper key={i} variant='outlined' sx={{ borderRadius: 2, overflow: 'hidden', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Skeleton variant='circular' width={20} height={20} sx={{ flexShrink: 0, mt: '2px' }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <Skeleton variant='text' width={160} height={18} />
              <Skeleton variant='rounded' width={48} height={18} sx={{ borderRadius: 10 }} />
            </Box>
            <Skeleton variant='text' width={100} height={14} />
            <Skeleton variant='text' width={220} height={14} />
          </Box>
          <Skeleton variant='rounded' width={20} height={20} />
        </Box>
      </Paper>
    ))}
  </Box>
);

const ManageShipmentItemsSearchBar = ({
  searchQuery,
  loading,
  refreshTooltip,
  onSearchChange,
  onRefresh,
  isRefreshDisabled,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
    <TextField
      size='small'
      fullWidth
      placeholder='Search by order ID or recipient'
      value={searchQuery}
      onChange={(e) => onSearchChange(e.target.value)}
      sx={{ '& .MuiInputBase-root': { fontSize: 13, height: 32, bgcolor: 'background.paper' } }}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            {loading ? (
              <CircularProgress size={13} />
            ) : (
              <SearchIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
            )}
          </InputAdornment>
        ),
      }}
    />
    <ManageShipmentItemsFilterButton />
    <Tooltip title={refreshTooltip}>
      <span>
        <IconButton size='small' onClick={onRefresh} disabled={isRefreshDisabled}>
          <RefreshIcon fontSize='small' />
        </IconButton>
      </span>
    </Tooltip>
  </Box>
);

ManageShipmentItemsSearchBar.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  refreshTooltip: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  isRefreshDisabled: PropTypes.bool,
};

ManageShipmentItemsSearchBar.defaultProps = {
  loading: false,
  refreshTooltip: '',
  isRefreshDisabled: false,
};

const ManageShipmentItemsLandingState = ({
  searchQuery,
  loading,
  onSearchChange,
  onRefresh,
  isRefreshDisabled,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    <Box sx={{ px: 2, pt: 1.5, pb: 1, bgcolor: 'background.paper', flexShrink: 0 }}>
      <ManageShipmentItemsSearchBar
        searchQuery={searchQuery}
        loading={loading}
        refreshTooltip='Search'
        onSearchChange={onSearchChange}
        onRefresh={onRefresh}
        isRefreshDisabled={isRefreshDisabled}
      />
    </Box>
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        width: '100%',
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          width: '100%',
          maxWidth: 320,
          mx: 'auto',
          '@keyframes landingFadeUp': {
            from: { opacity: 0, transform: 'translateY(16px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
          animation: 'landingFadeUp 0.4s ease-out',
        }}
      >
        <Box sx={{ display: 'inline-flex', mb: 2.5 }}>
          <Box
            sx={{
              width: 110,
              height: 110,
              borderRadius: '50%',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '@keyframes truckDriveIn': {
                '0%': { transform: 'translateX(-60px)', opacity: 0 },
                '60%': { transform: 'translateX(6px)', opacity: 1 },
                '100%': { transform: 'translateX(0)', opacity: 1 },
              },
              '& .MuiSvgIcon-root': {
                animation: 'truckDriveIn 0.6s ease-out forwards',
              },
            }}
          >
            <LocalShippingOutlinedIcon sx={{ fontSize: 58, color: 'grey.400' }} />
          </Box>
        </Box>

        <Typography
          variant='subtitle1'
          fontWeight={700}
          color='text.primary'
          sx={{ mb: 0.75, fontSize: 15 }}
        >
          No shipment selected
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.6 }}>
          Search or use filters to find a shipment, then select it to start moving items.
        </Typography>
      </Box>
    </Box>
  </Box>
);

ManageShipmentItemsLandingState.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  onSearchChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  isRefreshDisabled: PropTypes.bool,
};

ManageShipmentItemsLandingState.defaultProps = {
  loading: false,
  isRefreshDisabled: false,
};

const infoIconSx = { fontSize: 14, color: 'text.disabled', flexShrink: 0 };

const ManageShipmentItemsInfoCard = ({ shipment, fulfillingPharmacyName }) => {
  const addr = shipment.shipmentAddress;
  const addrStr = addr ? [addr.city, addr.state, addr.zip].filter(Boolean).join(', ') : '';
  const itemCount = (shipment.shipmentItems || []).length;
  const partnerPractice = [shipment.partnerName, shipment.practiceName]
    .filter(Boolean)
    .join(' · Practice: ');

  return (
    <Paper variant='outlined' sx={{ mx: 2, mt: 2, mb: 1, p: 1.5, borderRadius: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant='subtitle2' fontWeight={700}>
          Order: {shipment.referenceId || shipment.shipmentId}
        </Typography>
        <Chip
          label={`${itemCount} items`}
          size='small'
          sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {fulfillingPharmacyName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ComputerIcon sx={infoIconSx} />
            <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
              Facility
            </Typography>
            <Typography variant='caption' fontWeight={600} noWrap>
              {fulfillingPharmacyName}
            </Typography>
          </Box>
        )}
        {partnerPractice && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PeopleIcon sx={infoIconSx} />
            <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
              Partner
            </Typography>
            <Typography variant='caption' fontWeight={600} noWrap>
              {partnerPractice}
            </Typography>
          </Box>
        )}
        {shipment.recipientName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PersonIcon sx={infoIconSx} />
            <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
              Recipient
            </Typography>
            <Typography variant='caption' fontWeight={600} noWrap>
              {shipment.recipientName}
            </Typography>
          </Box>
        )}
        {addrStr && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <LocationOnIcon sx={infoIconSx} />
            <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
              Ship To
            </Typography>
            <Typography variant='caption' fontWeight={600} noWrap>
              {addrStr}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

ManageShipmentItemsInfoCard.propTypes = {
  shipment: PropTypes.shape({
    referenceId: PropTypes.string,
    shipmentId: PropTypes.string,
    shipmentAddress: PropTypes.shape({
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
    shipmentItems: PropTypes.arrayOf(PropTypes.shape({})),
    partnerName: PropTypes.string,
    practiceName: PropTypes.string,
    recipientName: PropTypes.string,
  }).isRequired,
  fulfillingPharmacyName: PropTypes.string,
};

ManageShipmentItemsInfoCard.defaultProps = {
  fulfillingPharmacyName: '',
};

const ManageShipmentItemsMoveAllControl = ({
  remainingCount,
  targetIds,
  getTargetRef,
  onMoveAll,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (e) => {
    if (targetIds.length === 1) {
      onMoveAll({ targetId: targetIds[0] });
    } else {
      setAnchorEl(e.currentTarget);
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1,
        py: 0.75,
        mb: 1,
        bgcolor: 'primary.50',
        border: '1px solid',
        borderColor: 'primary.light',
        borderRadius: 1,
      }}
    >
      <Typography variant='caption' color='primary.dark' fontWeight={600}>
        {remainingCount} item{remainingCount !== 1 ? 's' : ''} remaining
      </Typography>
      <Button
        size='small'
        variant='outlined'
        color='primary'
        startIcon={<MoveDownIcon fontSize='small' />}
        onClick={handleClick}
        sx={{ textTransform: 'none', fontSize: 11, py: 0.25 }}
      >
        Move All
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {targetIds.map((tId) => (
          <MenuItem
            key={tId}
            onClick={() => {
              setAnchorEl(null);
              onMoveAll({ targetId: tId });
            }}
          >
            <ListItemText
              primary={`Move to ${getTargetRef(tId) || tId}`}
              primaryTypographyProps={{ variant: 'body2' }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

ManageShipmentItemsMoveAllControl.propTypes = {
  remainingCount: PropTypes.number.isRequired,
  targetIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  getTargetRef: PropTypes.func.isRequired,
  onMoveAll: PropTypes.func.isRequired,
};

const ManageShipmentItemsConfirmDialog = ({
  open,
  saving,
  onClose,
  onConfirm,
  droppedItems,
  pickedItems,
  shipment,
  resolveShipmentData,
}) => {
  const outgoingGroups = Object.entries(droppedItems || {}).map(([targetId, move]) => {
    const target = resolveShipmentData(targetId);
    const items = (move.items || [])
      .map((mi) => (shipment.shipmentItems || []).find((i) => i.lineItemId === mi.referenceId))
      .filter(Boolean);
    return { targetId, targetRef: target?.referenceId || targetId, items };
  });

  const incomingBySource = (pickedItems || []).reduce((acc, item) => {
    const key = item.sourceShipmentId;
    if (!acc[key]) {
      const src = resolveShipmentData(key);
      acc[key] = { sourceRef: src?.referenceId || key, sourceShipment: src, items: [] };
    }
    acc[key].items.push(item);
    return acc;
  }, {});
  const incomingGroups = Object.entries(incomingBySource);

  const totalItems =
    outgoingGroups.reduce((s, g) => s + g.items.length, 0) + (pickedItems || []).length;
  const totalQty =
    outgoingGroups.reduce((s, g) => s + g.items.reduce((q, i) => q + (i.quantity || 0), 0), 0) +
    (pickedItems || []).reduce((s, i) => s + (i.quantity || 0), 0);
  const transferCount = outgoingGroups.length + incomingGroups.length;

  const renderItems = (items) => (
    <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
      {items.map((item, idx) => (
        <Box
          key={item.lineItemId || item.referenceId || item.orderId || idx}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1,
            borderBottom: idx < items.length - 1 ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              bgcolor: 'grey.100',
              borderRadius: 1,
              p: 0.6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='body2' fontWeight={600} color='text.primary' noWrap>
              {item.name || item.sku}
            </Typography>
            {item.orderId && (
              <Typography variant='caption' color='text.secondary' noWrap>
                Order: {item.orderId}
              </Typography>
            )}
          </Box>
          <Typography variant='body2' color='text.secondary' sx={{ flexShrink: 0 }}>
            Qty: {item.quantity}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const renderCard = ({ key, fromRef, toRef, items }) => (
    <Box key={key} sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 0.75,
        }}
      >
        <Typography variant='body2' color='text.secondary' fontWeight={500}>
          {fromRef}
        </Typography>
        <ArrowForwardIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CheckCircleIcon sx={{ fontSize: 15, color: '#16a34a' }} />
          <Typography variant='body2' color='#16a34a' fontWeight={700}>
            {toRef}
          </Typography>
        </Box>
        <Typography variant='caption' color='text.disabled' sx={{ ml: 'auto', flexShrink: 0 }}>
          {items.length} item{items.length !== 1 ? 's' : ''}
        </Typography>
      </Box>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          px: 2,
          py: 0.25,
          bgcolor: 'background.paper',
        }}
      >
        {renderItems(items)}
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              bgcolor: 'primary.main',
              borderRadius: 1.5,
              p: 0.875,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <SwapHorizIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant='subtitle1' fontWeight={700} sx={{ lineHeight: 1.2 }}>
              Confirm Item Transfer
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {totalItems} item{totalItems !== 1 ? 's' : ''} · Total Qty: {totalQty}
            </Typography>
          </Box>
          <IconButton size='small' onClick={onClose} disabled={saving}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 0, pb: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Warning — fixed, never scrolls */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            px: 1.5,
            py: 1.25,
            mt: 2,
            mb: 2,
            flexShrink: 0,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 16, color: '#f59e0b', flexShrink: 0, mt: '2px' }} />
          <Box>
            <Typography variant='body2' fontWeight={700} color='text.primary'>
              Are you sure you want to save these changes?
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25 }}>
              This action cannot be undone. Items will be permanently moved between shipments. Any
              shipment that has all its items removed will be automatically cancelled.
            </Typography>
          </Box>
        </Box>

        {/* Section label — fixed */}
        <Typography
          variant='subtitle2'
          fontWeight={700}
          color='text.primary'
          sx={{ mb: 1, flexShrink: 0 }}
        >
          Items to be moved
        </Typography>

        {/* Scrollable transfer cards */}
        <Box sx={{ overflowY: 'auto', maxHeight: 360, flex: 1, pb: 1 }}>
          {outgoingGroups.map((group) =>
            renderCard({
              key: group.targetId,
              fromRef: shipment.referenceId,
              toRef: group.targetRef,
              items: group.items,
            })
          )}
          {incomingGroups.map(([sourceId, group]) =>
            renderCard({
              key: sourceId,
              fromRef: group.sourceRef,
              toRef: shipment.referenceId,
              items: group.items,
            })
          )}
        </Box>

        {/* Summary footer — fixed */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            px: 2,
            py: 1,
            mt: 1,
            mb: 1,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <SyncAltIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant='body2' color='text.secondary'>
              {transferCount} transfer{transferCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Typography variant='body2' color='text.primary' fontWeight={600}>
            {totalItems} item{totalItems !== 1 ? 's' : ''} · Total Qty: {totalQty}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <LoadingButton
          onClick={onConfirm}
          loading={saving}
          variant='contained'
          color='primary'
          sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

ManageShipmentItemsConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  droppedItems: PropTypes.objectOf(
    PropTypes.shape({
      items: PropTypes.arrayOf(PropTypes.shape({ referenceId: PropTypes.string })),
      targetReferenceId: PropTypes.string,
    })
  ),
  pickedItems: PropTypes.arrayOf(
    PropTypes.shape({ sourceShipmentId: PropTypes.string, quantity: PropTypes.number })
  ),
  shipment: PropTypes.shape({
    referenceId: PropTypes.string,
    shipmentItems: PropTypes.arrayOf(PropTypes.shape({ lineItemId: PropTypes.string })),
  }).isRequired,
  resolveShipmentData: PropTypes.func.isRequired,
};

ManageShipmentItemsConfirmDialog.defaultProps = {
  droppedItems: {},
  pickedItems: [],
};

const ManageShipmentItemsFooter = ({ totalItemCount, totalQty, hasChanges, saving, onSave }) => (
  <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: 'grey.100',
        borderRadius: 2,
        px: 2,
        py: 1.25,
        minWidth: 160,
      }}
    >
      <Box
        sx={{
          bgcolor: 'primary.main',
          borderRadius: 1.5,
          p: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <ListAltIcon sx={{ color: 'white', fontSize: 20 }} />
      </Box>
      <Box>
        <Typography variant='caption' color='text.secondary'>
          Items to Move
        </Typography>
        <Typography variant='body2' fontWeight={700}>
          {totalItemCount} items
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          Total Qty: {totalQty}
        </Typography>
      </Box>
    </Box>
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 1,
        px: 2,
        py: 1.25,
      }}
    >
      <InfoOutlinedIcon sx={{ color: 'primary.main', fontSize: 18, flexShrink: 0, mt: '2px' }} />
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
          <Typography variant='caption' fontWeight={700} color='primary.main'>
            How it works
          </Typography>
          <Typography variant='caption' color='text.disabled'>
            — Drag items between shipments using the rules below
          </Typography>
        </Box>
        {[
          'Drag items left → right to move them to other shipments (Outgoing — red)',
          'Drag items right → left to bring them into this shipment (Incoming — green)',
          // eslint-disable-next-line max-len
          'Only one direction is allowed per session — choose either Outgoing or Incoming before saving',
          `Each shipment can hold a maximum of ${MAX_ITEMS_PER_SHIPMENT} items — moves exceeding this limit will be blocked`,
        ].map((line) => (
          <Box key={line} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Typography variant='caption' color='primary.main' sx={{ lineHeight: 1.6 }}>
              •
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.6 }}>
              {line}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
      <Button
        variant='contained'
        onClick={onSave}
        disabled={!hasChanges || saving}
        endIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
        sx={{
          px: 3,
          py: 1.1,
          fontWeight: 700,
          fontSize: 14,
          borderRadius: 2,
          textTransform: 'none',
          boxShadow: hasChanges ? '0 4px 14px rgba(0,0,0,0.2)' : 'none',
          '&.Mui-disabled': {
            background: 'linear-gradient(135deg, #e0e0e0 0%, #eeeeee 100%)',
            color: '#bdbdbd',
            boxShadow: 'none',
          },
        }}
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </Button>
    </Box>
  </Box>
);

ManageShipmentItemsFooter.propTypes = {
  totalItemCount: PropTypes.number.isRequired,
  totalQty: PropTypes.number.isRequired,
  hasChanges: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
};

// ───────────────────────────────────────────────────────────────────────────────

const ManageShipmentItemsDialog = ({
  open,
  shipment,
  fulfillingPharmacyName,
  onClose,
  onSuccess,
  onAllItemsMoved,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const dividerBorder = `1px solid ${theme.palette.divider}`;
  const [enabled] = useStrictDroppable(!open);
  const [currentItems, setCurrentItems] = useState([]);
  const [otherShipments, setOtherShipments] = useState([]);
  const [droppedItems, setDroppedItems] = useState({});
  const [pickedItems, setPickedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removeConfirmItem, setRemoveConfirmItem] = useState(null);
  const [error, setError] = useState(null);
  const [draggingFromTarget, setDraggingFromTarget] = useState(false);
  const [isDraggingFromLeft, setIsDraggingFromLeft] = useState(false);
  const [expandedTargetId, setExpandedTargetId] = useState(null);
  const [collapsedPinnedIds, setCollapsedPinnedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [leftSearch, setLeftSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [shipmentsPage, setShipmentsPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);
  const [shipmentsTotalCount, setShipmentsTotalCount] = useState(0);
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  const searchDebounceRef = useRef(null);
  const incomingItemsRef = useRef(null);
  const [filters, setFilters] = useState(emptyFilter);
  const currentTenantId = useSelector(({ auth }) => auth.currentTenantId);
  const fulfillingPharmacies = useSelector(({ pharmacy }) => pharmacy[currentTenantId] || []);

  const loadShipments = (params = {}) => {
    const { page = 1 } = params;
    setHasInteracted(true);
    setLoading(true);
    setError(null);
    getOrderShipmentsApi({
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      sortBy: { sortKey: 'createdAt', sortOrder: 'ASC' },
      filter: { shipmentStatus: ['REQUEST_CREATED'] },
    })
      .then(({ result, count }) => {
        setOtherShipments(filter(result, (s) => s.shipmentId !== shipment.shipmentId));
        setShipmentsTotalCount(count || 0);
        setShipmentsPage(page);
      })
      .catch(() => setError('Failed to load other shipments.'))
      .finally(() => setLoading(false));
  };

  const loadSearchPage = (params = {}) => {
    const { page = 1, query = searchQuery, activeFilters = filters } = params;
    setSearchLoading(true);
    const apiFilter = { shipmentStatus: ['REQUEST_CREATED'] };
    if (query) apiFilter.flatSearch = query;
    if (activeFilters.recipientName) apiFilter.recipientName = activeFilters.recipientName;
    if (activeFilters.fulfillingAt) apiFilter.fulfillingPharmacyIds = [activeFilters.fulfillingAt];
    if (activeFilters.isManual === 'manual') apiFilter.isManualShipment = true;
    if (activeFilters.isManual === 'non-manual') apiFilter.isManualShipment = false;
    const manualAddr = {};
    if (activeFilters.city) manualAddr.city = activeFilters.city;
    if (activeFilters.state) manualAddr.state = activeFilters.state;
    if (activeFilters.zip) manualAddr.zip = activeFilters.zip;
    if (activeFilters.matchPartner && shipment.partnerId)
      apiFilter.partnerIds = [shipment.partnerId];
    if (activeFilters.matchPractice && shipment.practiceId)
      apiFilter.practiceIds = [shipment.practiceId];
    if (Object.keys(manualAddr).length > 0) apiFilter.address = manualAddr;
    getOrderShipmentsApi({
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      sortBy: { sortKey: 'createdAt', sortOrder: 'ASC' },
      filter: apiFilter,
    })
      .then(({ result, count }) => {
        setSearchResults(filter(result, (s) => s.shipmentId !== shipment.shipmentId));
        setSearchTotalCount(count || 0);
        setSearchPage(page);
      })
      .catch(() => setError('Search failed. Please try again.'))
      .finally(() => setSearchLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    setCurrentItems(shipment.shipmentItems || []);
    setDroppedItems({});
    setPickedItems([]);
    setSearchQuery('');
    setLeftSearch('');
    setSearchResults(null);
    setExpandedTargetId(null);
    setCollapsedPinnedIds(new Set());
    setError(null);
    setConfirmOpen(false);
    setRemoveConfirmItem(null);
    setShipmentsPage(1);
    setSearchPage(1);
    setShipmentsTotalCount(0);
    setSearchTotalCount(0);
    setFilters(emptyFilter);
    setOtherShipments([]);
    setHasInteracted(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const runApiWithFilters = useCallback(({ query, activeFilters, debounce = false }) => {
    clearTimeout(searchDebounceRef.current);
    const hasQuery = query?.length > 0;
    const hasFilters =
      activeFilters.recipientName ||
      activeFilters.fulfillingAt ||
      activeFilters.isManual ||
      activeFilters.matchPartner ||
      activeFilters.matchPractice ||
      activeFilters.city ||
      activeFilters.state ||
      activeFilters.zip;
    if (!hasQuery && !hasFilters) {
      setSearchResults(null);
      setOtherShipments([]);
      return;
    }
    const doCall = () => {
      setHasInteracted(true);
      loadSearchPage({ page: 1, query, activeFilters });
    };
    if (debounce) {
      searchDebounceRef.current = setTimeout(doCall, 1000);
    } else {
      doCall();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    runApiWithFilters({ query: searchQuery, activeFilters: filters, debounce: true });
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      runApiWithFilters({ query: searchQuery, activeFilters: newFilters });
    },
    [searchQuery, runApiWithFilters]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const removeFilter = useCallback(
    (key) => {
      const boolKeys = ['matchPartner', 'matchPractice'];
      const newFilters = { ...filters, [key]: boolKeys.includes(key) ? false : '' };
      setFilters(newFilters);
      runApiWithFilters({ query: searchQuery, activeFilters: newFilters });
    },
    [filters, searchQuery, runApiWithFilters]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const pinnedShipmentIdRef = useRef(null);
  const activeShipmentDataRef = useRef({});

  useEffect(() => {
    if ((searchResults !== null || searchLoading || loading) && pinnedShipmentIdRef.current) {
      setExpandedTargetId(null);
      setCollapsedPinnedIds((prev) => {
        const next = new Set(prev);
        next.add(pinnedShipmentIdRef.current);
        return next;
      });
    }
  }, [searchResults, searchLoading, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (pickedItems.length > 0 && incomingItemsRef.current) {
      let el = incomingItemsRef.current.parentElement;
      while (el) {
        if (el.scrollHeight > el.clientHeight) {
          el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
          break;
        }
        el = el.parentElement;
      }
    }
  }, [pickedItems.length]);

  const baseShipments = searchResults !== null ? searchResults : otherShipments;

  const onDragEnd = ({ source, destination, draggableId }) => {
    setDraggingFromTarget(false);
    setIsDraggingFromLeft(false);
    if (!destination || destination.droppableId === source.droppableId) return;

    if (draggableId.startsWith('pick-') && destination.droppableId === DROPPABLE_CURRENT) {
      if (!isEmpty(droppedItems)) return;
      const lineItemId = draggableId.slice(5);
      const src = resolveShipment({
        shipmentId: source.droppableId,
        otherShipments,
        searchResults,
      });
      const item = src?.shipmentItems?.find((i) => i.lineItemId === lineItemId);
      if (item && src) {
        if (currentItems.length + pickedItems.length + 1 > MAX_ITEMS_PER_SHIPMENT) {
          enqueueSnackbar(
            `Cannot add item — this shipment would exceed the ${MAX_ITEMS_PER_SHIPMENT}-item limit.`,
            { variant: 'error', autoHideDuration: 4000 }
          );
          return;
        }
        setPickedItems((prev) => [
          ...prev,
          { ...item, sourceShipmentId: src.shipmentId, sourceReferenceId: src.referenceId },
        ]);
      }
      return;
    }

    if (source.droppableId === DROPPABLE_CURRENT) {
      if (!isEmpty(pickedItems)) return;
      const { droppableId: targetId } = destination;
      const movedItem = currentItems.find((i) => i.lineItemId === draggableId);
      const targetShipment = resolveShipment({
        shipmentId: targetId,
        otherShipments,
        searchResults,
      });
      if (!movedItem || !targetShipment) return;
      const targetCurrentCount = (targetShipment.shipmentItems || []).length;
      const targetDroppedCount = droppedItems[targetId]?.items?.length ?? 0;
      if (targetCurrentCount + targetDroppedCount + 1 > MAX_ITEMS_PER_SHIPMENT) {
        enqueueSnackbar(
          `Cannot move item — target shipment would exceed the ${MAX_ITEMS_PER_SHIPMENT}-item limit.`,
          { variant: 'error', autoHideDuration: 4000 }
        );
        return;
      }
      setCurrentItems((prev) => prev.filter((i) => i.lineItemId !== draggableId));
      setDroppedItems((prev) => ({
        ...prev,
        [targetId]: {
          targetReferenceId: targetShipment.referenceId,
          items: [...(prev[targetId]?.items || []), { referenceId: movedItem.lineItemId }],
        },
      }));
      setCollapsedPinnedIds((prev) => {
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
      setExpandedTargetId(targetId);
      return;
    }

    if (destination.droppableId === DROPPABLE_CURRENT) {
      setDroppedItems((prev) =>
        removeItemFromDrops({
          droppedItems: prev,
          targetId: source.droppableId,
          referenceId: draggableId,
        })
      );
      const originalItem = (shipment.shipmentItems || []).find((i) => i.lineItemId === draggableId);
      if (originalItem) setCurrentItems((prev) => [...prev, originalItem]);
    }
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    const allItemsMoved = currentItems.length === 0 && pickedItems.length === 0;
    try {
      const incomingBySource = pickedItems.reduce((acc, item) => {
        const key = item.sourceShipmentId;
        if (!acc[key]) acc[key] = { ref: item.sourceReferenceId, items: [] };
        acc[key].items.push({ referenceId: item.lineItemId });
        return acc;
      }, {});

      const transfers = [
        ...Object.entries(droppedItems).map(([tId, move]) => ({
          toShipmentId: tId,
          toReferenceId: move.targetReferenceId,
          fromShipmentId: shipment.shipmentId,
          fromReferenceId: shipment.referenceId,
          items: move.items,
        })),
        ...Object.entries(incomingBySource).map(([sId, group]) => ({
          toShipmentId: shipment.shipmentId,
          toReferenceId: shipment.referenceId,
          fromShipmentId: sId,
          fromReferenceId: group.ref,
          items: group.items,
        })),
      ];

      await updateShipmentItemsApi({ transfers });
      const movedOutIds = new Set(
        Object.values(droppedItems).flatMap((m) => m.items.map((i) => i.referenceId))
      );
      setConfirmOpen(false);
      enqueueSnackbar('Items moved successfully.', { variant: 'success' });
      onSuccess(movedOutIds, pickedItems);
      onClose();
      if (allItemsMoved && onAllItemsMoved) onAllItemsMoved();
    } catch {
      setConfirmOpen(false);
      enqueueSnackbar('Failed to update items. Please try again.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const onConfirmRemoveFromBatch = async () => {
    if (!removeConfirmItem) return;
    try {
      await splitShipmentItemsApi({
        shipmentId: shipment.shipmentId,
        referenceId: shipment.referenceId,
        itemReferenceIds: [removeConfirmItem.lineItemId],
      });
      enqueueSnackbar('Item removed. A new shipment has been created.', { variant: 'success' });
      const removedIds = new Set([removeConfirmItem.lineItemId]);
      setRemoveConfirmItem(null);
      onSuccess(removedIds, []);
      onClose();
    } catch {
      enqueueSnackbar('Failed to remove item. Please try again.', { variant: 'error' });
    }
  };

  const moveAllToTarget = ({ targetId }) => {
    if (!targetId || isEmpty(currentItems)) return;
    const targetShipment = resolveShipment({ shipmentId: targetId, otherShipments, searchResults });
    if (!targetShipment) return;
    const targetCurrentCount = (targetShipment.shipmentItems || []).length;
    const targetDroppedCount = droppedItems[targetId]?.items?.length ?? 0;
    if (targetCurrentCount + targetDroppedCount + currentItems.length > MAX_ITEMS_PER_SHIPMENT) {
      enqueueSnackbar(
        `Cannot move all items — target shipment would exceed the ${MAX_ITEMS_PER_SHIPMENT}-item limit.`,
        { variant: 'error', autoHideDuration: 4000 }
      );
      return;
    }
    setDroppedItems((prev) => ({
      ...prev,
      [targetId]: {
        targetReferenceId: targetShipment.referenceId,
        items: [
          ...(prev[targetId]?.items || []),
          ...currentItems.map((i) => ({ referenceId: i.lineItemId })),
        ],
      },
    }));
    setCurrentItems([]);
  };

  const moveAllFromSource = (sourceShipmentId) => {
    const src = baseShipments.find((s) => s.shipmentId === sourceShipmentId);
    if (!src) return;
    const alreadyPickedIds = new Set(pickedItems.map((i) => i.lineItemId));
    const remaining = (src.shipmentItems || []).filter((i) => !alreadyPickedIds.has(i.lineItemId));
    const available = MAX_ITEMS_PER_SHIPMENT - currentItems.length - pickedItems.length;
    if (available <= 0) {
      enqueueSnackbar(
        `Cannot add items — this shipment has reached the ${MAX_ITEMS_PER_SHIPMENT}-item limit.`,
        { variant: 'error', autoHideDuration: 4000 }
      );
      return;
    }
    const itemsToAdd = remaining.slice(0, available);
    setPickedItems((prev) => [
      ...prev,
      ...itemsToAdd.map((i) => ({
        ...i,
        sourceShipmentId: src.shipmentId,
        sourceReferenceId: src.referenceId,
      })),
    ]);
  };

  const undoSingleItem = ({ shipmentId: tId, referenceId }) => {
    setDroppedItems((prev) =>
      removeItemFromDrops({ droppedItems: prev, targetId: tId, referenceId })
    );
    const originalItem = (shipment.shipmentItems || []).find((i) => i.lineItemId === referenceId);
    if (originalItem) setCurrentItems((prev) => [...prev, originalItem]);
  };

  const undoAllItems = ({ shipmentId: tId }) => {
    const move = droppedItems[tId];
    if (!move) return;
    const restoredItems = resolveOriginalItems({
      dropItems: move.items,
      shipmentItems: shipment.shipmentItems,
    });
    setDroppedItems((prev) => removeAllFromDrops({ droppedItems: prev, targetId: tId }));
    if (restoredItems.length > 0) setCurrentItems((prev) => [...prev, ...restoredItems]);
  };

  const movedOutItemIds = new Set(
    Object.values(droppedItems).flatMap((move) => move.items.map((i) => i.referenceId))
  );
  const movedOutItems = (shipment.shipmentItems || []).filter((i) =>
    movedOutItemIds.has(i.lineItemId)
  );

  const hasChanges = !isEmpty(droppedItems) || !isEmpty(pickedItems);

  const activeShipmentIds = !isEmpty(droppedItems)
    ? Object.keys(droppedItems)
    : [...new Set(pickedItems.map((i) => i.sourceShipmentId))];

  pinnedShipmentIdRef.current = activeShipmentIds[0] || null;

  activeShipmentIds.forEach((id) => {
    const found = resolveShipment({ shipmentId: id, otherShipments, searchResults });
    if (found) activeShipmentDataRef.current[id] = found;
  });
  if (activeShipmentIds.length === 0) activeShipmentDataRef.current = {};

  const isFilterActive = searchResults !== null || searchLoading;
  const pinnedShipmentIds = activeShipmentIds.filter((id) => {
    const isInCurrentList = baseShipments.some((s) => s.shipmentId === id);
    return isFilterActive || loading || !isInCurrentList;
  });

  const pinnedShipments = pinnedShipmentIds
    .map((id) => {
      const live = resolveShipment({ shipmentId: id, otherShipments, searchResults });
      return live || activeShipmentDataRef.current[id] || null;
    })
    .filter(Boolean);

  const pinnedRole = !isEmpty(droppedItems) ? 'destination' : 'source';

  const pinnedShipmentIdSet = new Set(pinnedShipmentIds);
  const displayedShipments = baseShipments.filter((s) => !pinnedShipmentIdSet.has(s.shipmentId));

  const isTargetDisabled = draggingFromTarget || !isEmpty(pickedItems);

  const getTargetRef = (tId) =>
    droppedItems[tId]?.targetReferenceId ||
    baseShipments.find((s) => s.shipmentId === tId)?.referenceId ||
    otherShipments.find((s) => s.shipmentId === tId)?.referenceId;

  const contextValue = useMemo(
    () => ({ filters, fulfillingPharmacies, shipment, applyFilters, removeFilter }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, fulfillingPharmacies, shipment, applyFilters, removeFilter]
  );

  const totalItemCount =
    Object.values(droppedItems).reduce((sum, m) => sum + m.items.length, 0) + pickedItems.length;

  const totalQty = [
    ...Object.values(droppedItems).flatMap((m) =>
      m.items.map(
        (i) =>
          (shipment.shipmentItems || []).find((s) => s.lineItemId === i.referenceId)?.quantity || 0
      )
    ),
    ...pickedItems.map((i) => i.quantity || 0),
  ].reduce((sum, q) => sum + Number(q), 0);

  const buildAccordionProps = (targetShipment) => ({
    targetShipment,
    droppedHere: droppedItems[targetShipment.shipmentId]?.items || [],
    originalItems: shipment.shipmentItems || [],
    isDropDisabled: isTargetDisabled,
    pickedItems,
    fulfillingPharmacyName: (() => {
      const p = fulfillingPharmacies.find(
        (ph) => ph.fulfillingPharmacyId === targetShipment.fulfillingPharmacyId
      );
      if (!p) return '';
      return p.state ? `${p.name} - ${p.state}` : p.name;
    })(),
    hasDroppedItems: !isEmpty(droppedItems),
    isPickLocked: !isEmpty(droppedItems),
    isManualShipment: targetShipment.isManualShipment,
    isDraggingFromTarget: draggingFromTarget,
    isDraggingFromLeft,
    onMoveAll: isEmpty(droppedItems)
      ? () => moveAllFromSource(targetShipment.shipmentId)
      : undefined,
    onUndoItem: (referenceId) =>
      undoSingleItem({ shipmentId: targetShipment.shipmentId, referenceId }),
    onUndoAll: () => undoAllItems({ shipmentId: targetShipment.shipmentId }),
  });

  const isPinnedExpanded = (targetShipment) =>
    (!!droppedItems[targetShipment.shipmentId]?.items?.length &&
      !collapsedPinnedIds.has(targetShipment.shipmentId)) ||
    expandedTargetId === targetShipment.shipmentId;

  const onPinnedToggle = (targetShipment) => {
    const { shipmentId } = targetShipment;
    const isExpanded = isPinnedExpanded(targetShipment);
    setCollapsedPinnedIds((prev) => {
      const next = new Set(prev);
      if (isExpanded) next.add(shipmentId);
      else next.delete(shipmentId);
      return next;
    });
    setExpandedTargetId(isExpanded ? null : shipmentId);
  };

  const filteredCurrentItems = useMemo(() => {
    if (!leftSearch) return currentItems;
    const q = leftSearch.toLowerCase();
    return currentItems.filter(
      (i) =>
        i.name?.toLowerCase().includes(q) ||
        i.sku?.toLowerCase().includes(q) ||
        i.orderId?.toLowerCase().includes(q) ||
        i.lineItemId?.toLowerCase().includes(q)
    );
  }, [currentItems, leftSearch]);

  const pickedItemsBySource = useMemo(
    () =>
      pickedItems.reduce((acc, item) => {
        const key = item.sourceShipmentId;
        if (!acc[key]) acc[key] = { referenceId: item.sourceReferenceId, items: [] };
        acc[key].items.push(item);
        return acc;
      }, {}),
    [pickedItems]
  );

  const resetAll = useCallback(() => {
    setCurrentItems(shipment.shipmentItems || []);
    setDroppedItems({});
    setPickedItems([]);
    setExpandedTargetId(null);
  }, [shipment.shipmentItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const paginationIsSearch = searchResults !== null;
  const paginationTotalCount = paginationIsSearch ? searchTotalCount : shipmentsTotalCount;
  const paginationCurrentPage = paginationIsSearch ? searchPage : shipmentsPage;
  const paginationTotalPages = Math.ceil(paginationTotalCount / PAGE_SIZE);
  const paginationBar =
    !loading && !searchLoading && paginationTotalPages > 1 ? (
      <Box
        sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1, borderTop: dividerBorder }}
      >
        <Pagination
          count={paginationTotalPages}
          page={paginationCurrentPage}
          size='small'
          onChange={(_, newPage) => {
            if (paginationIsSearch) {
              loadSearchPage({ page: newPage });
            } else {
              loadShipments({ page: newPage });
            }
          }}
        />
      </Box>
    ) : null;

  return (
    <ManageShipmentItemsContext.Provider value={contextValue}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='lg'
        fullWidth
        sx={{ '& .MuiDialog-paper': { height: '90vh' } }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                bgcolor: '#1e2a4a',
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SwapHorizIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant='h6' fontWeight={600} lineHeight={1}>
                Manage Shipment Items
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', lineHeight: 1.2, mt: 0.25 }}
              >
                Move items between shipments quickly and easily.
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={4000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity='error'
            onClose={() => setError(null)}
            sx={{ width: '100%', boxShadow: 3 }}
          >
            {error}
          </Alert>
        </Snackbar>
        <DialogContent
          sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
        >
          <DragDropContext
            onBeforeDragStart={(ev) => {
              if (ev.source.droppableId === DROPPABLE_CURRENT) {
                flushSync(() => setIsDraggingFromLeft(true));
              }
            }}
            onDragStart={(ev) => {
              const fromTarget = ev.source.droppableId !== DROPPABLE_CURRENT;
              setDraggingFromTarget(fromTarget);
            }}
            onDragEnd={onDragEnd}
          >
            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
              {/* Left panel — Current Shipment */}
              <Box
                sx={{
                  width: '40%',
                  borderRight: dividerBorder,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <ManageShipmentItemsInfoCard
                  shipment={shipment}
                  fulfillingPharmacyName={fulfillingPharmacyName}
                />

                <Box
                  sx={{
                    px: 2,
                    pt: 1.5,
                    pb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Typography variant='body2' fontWeight={700} sx={{ flexShrink: 0 }}>
                    Items in this shipment
                  </Typography>
                  <TextField
                    size='small'
                    placeholder='Search items...'
                    value={leftSearch}
                    onChange={(e) => setLeftSearch(e.target.value)}
                    sx={{
                      width: 220,
                      '& .MuiInputBase-root': {
                        fontSize: 12,
                        height: 28,
                        bgcolor: 'background.paper',
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <SearchIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    position: 'relative',
                    flex: 1,
                    minHeight: 0,
                    mx: 2,
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 32,
                      background: 'linear-gradient(to bottom, transparent, white)',
                      borderRadius: '0 0 4px 4px',
                      pointerEvents: 'none',
                      zIndex: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      overflow: 'auto',
                      overflowX: 'hidden',
                      px: 1,
                      pb: 1.5,
                      scrollbarWidth: 'none',
                      '&::-webkit-scrollbar': { display: 'none' },
                    }}
                  >
                    {enabled && (
                      <Droppable droppableId={DROPPABLE_CURRENT}>
                        {(provided, snapshot) => (
                          <Box
                            ref={provided.innerRef}
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...provided.droppableProps}
                            sx={{
                              minHeight: '100%',
                              border: snapshot.isDraggingOver
                                ? '2px dashed'
                                : '1px solid transparent',
                              borderColor: snapshot.isDraggingOver ? 'primary.main' : 'transparent',
                              bgcolor: snapshot.isDraggingOver ? 'primary.50' : 'background.paper',
                              borderRadius: 1,
                              transition: 'all 0.2s ease',
                              p: 1,
                            }}
                          >
                            {!isEmpty(droppedItems) && currentItems.length > 0 && (
                              <ManageShipmentItemsMoveAllControl
                                remainingCount={currentItems.length}
                                targetIds={Object.keys(droppedItems)}
                                getTargetRef={getTargetRef}
                                onMoveAll={moveAllToTarget}
                              />
                            )}
                            {isEmpty(currentItems) &&
                            isEmpty(pickedItems) &&
                            isEmpty(movedOutItems) ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: '100%',
                                  minHeight: 80,
                                  gap: 0.75,
                                }}
                              >
                                <InboxIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                                <Typography variant='body2' fontWeight={500} color='text.disabled'>
                                  All items have been moved
                                </Typography>
                              </Box>
                            ) : (
                              <>
                                {map(filteredCurrentItems, (item, index) => (
                                  <ItemRow
                                    key={item.lineItemId}
                                    item={item}
                                    index={index}
                                    isDraggable={isEmpty(pickedItems) && !loading}
                                  />
                                ))}
                                {!isEmpty(droppedItems) && (
                                  <>
                                    <Box
                                      sx={{
                                        px: 1,
                                        py: 0.75,
                                        mt: 1.5,
                                        borderTop: '1px solid',
                                        borderBottom: '1px solid',
                                        borderColor: '#d32f2f',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                      }}
                                    >
                                      <Typography
                                        variant='caption'
                                        sx={{ letterSpacing: 0.3, color: '#d32f2f' }}
                                        fontWeight={700}
                                      >
                                        Outgoing Items
                                      </Typography>
                                      <Chip
                                        label={movedOutItems.length}
                                        size='small'
                                        variant='outlined'
                                        sx={{
                                          height: 18,
                                          fontSize: 10,
                                          fontWeight: 700,
                                          color: '#d32f2f',
                                          borderColor: '#d32f2f',
                                          '& .MuiChip-label': { px: 0.75 },
                                        }}
                                      />
                                    </Box>
                                    {Object.entries(droppedItems).map(([tId, move]) => {
                                      const destRef = getTargetRef(tId);
                                      return (
                                        <Box key={tId}>
                                          <Typography
                                            variant='caption'
                                            sx={{
                                              display: 'block',
                                              px: 1,
                                              pt: 0.5,
                                              color: '#d32f2f',
                                            }}
                                          >
                                            To Order: <strong>{destRef}</strong>
                                          </Typography>
                                          {move.items.map((mi) => {
                                            const item = (shipment.shipmentItems || []).find(
                                              (i) => i.lineItemId === mi.referenceId
                                            );
                                            if (!item) return null;
                                            return (
                                              <Box
                                                key={`moved-${item.lineItemId}`}
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 1,
                                                  px: 1,
                                                  py: 0.75,
                                                  opacity: 0.45,
                                                  borderRadius: 1,
                                                }}
                                              >
                                                <DragIndicatorIcon
                                                  sx={{ color: 'transparent', fontSize: 16 }}
                                                />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                  <Typography
                                                    variant='body2'
                                                    sx={{
                                                      textDecoration: 'line-through',
                                                      color: 'text.disabled',
                                                    }}
                                                  >
                                                    {item.name || item.sku}
                                                  </Typography>
                                                  <Typography
                                                    variant='caption'
                                                    color='text.disabled'
                                                  >
                                                    {item.orderId && (
                                                      <>Order: {item.orderId}&nbsp;·&nbsp;</>
                                                    )}
                                                    Qty: {item.quantity}
                                                  </Typography>
                                                </Box>
                                                <Chip
                                                  label='Moved'
                                                  size='small'
                                                  variant='outlined'
                                                  sx={{
                                                    height: 20,
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: '#d32f2f',
                                                    borderColor: '#d32f2f',
                                                    '& .MuiChip-label': { px: 1 },
                                                  }}
                                                />
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      );
                                    })}
                                  </>
                                )}
                                {!isEmpty(pickedItems) && (
                                  <>
                                    <Box
                                      ref={incomingItemsRef}
                                      sx={{
                                        px: 1,
                                        py: 0.75,
                                        mt: 1.5,
                                        bgcolor: 'success.50',
                                        borderTop: '1px solid',
                                        borderBottom: '1px solid',
                                        borderColor: 'success.light',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                        }}
                                      >
                                        <Typography
                                          variant='caption'
                                          color='success.dark'
                                          fontWeight={700}
                                          sx={{ letterSpacing: 0.3 }}
                                        >
                                          Incoming Items
                                        </Typography>
                                        <Chip
                                          label={pickedItems.length}
                                          size='small'
                                          color='success'
                                          sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                                        />
                                      </Box>
                                    </Box>
                                    {Object.entries(pickedItemsBySource).map(
                                      ([sourceId, group]) => (
                                        <Box key={sourceId}>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              px: 1,
                                              pt: 0.5,
                                            }}
                                          >
                                            <Typography
                                              variant='caption'
                                              color='success.dark'
                                              fontWeight={600}
                                            >
                                              From Order: <strong>{group.referenceId}</strong>
                                            </Typography>
                                            <Button
                                              size='small'
                                              onClick={() =>
                                                setPickedItems((prev) =>
                                                  prev.filter(
                                                    (i) => i.sourceShipmentId !== sourceId
                                                  )
                                                )
                                              }
                                              sx={{
                                                fontSize: 10,
                                                textTransform: 'none',
                                                color: 'text.secondary',
                                                p: 0,
                                                minWidth: 0,
                                                lineHeight: 1,
                                              }}
                                            >
                                              Clear
                                            </Button>
                                          </Box>
                                          {group.items.map((item) => {
                                            const globalIdx = pickedItems.findIndex(
                                              (i) => i.lineItemId === item.lineItemId
                                            );
                                            return (
                                              <Box
                                                key={item.lineItemId}
                                                sx={{ position: 'relative' }}
                                              >
                                                <ItemRow
                                                  item={item}
                                                  index={currentItems.length + globalIdx}
                                                  isDraggable={false}
                                                />
                                                <IconButton
                                                  size='small'
                                                  title='Undo — move back to original shipment'
                                                  onClick={() =>
                                                    setPickedItems((prev) =>
                                                      prev.filter(
                                                        (i) => i.lineItemId !== item.lineItemId
                                                      )
                                                    )
                                                  }
                                                  sx={{
                                                    position: 'absolute',
                                                    right: 6,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                  }}
                                                >
                                                  <UndoIcon fontSize='small' />
                                                </IconButton>
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      )
                                    )}
                                  </>
                                )}
                              </>
                            )}
                            {provided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Right panel — Other Shipments */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, bgcolor: 'background.paper' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      mb: 0.25,
                    }}
                  >
                    <Box>
                      <Typography variant='subtitle1' fontWeight={700} color='text.primary'>
                        Move items to another shipment
                      </Typography>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ display: 'block', lineHeight: 1.4 }}
                      >
                        Only open, non-shipped orders are eligible. Filter the open shipments below,
                        then drag items between shipments.
                      </Typography>
                    </Box>
                    {hasChanges && (
                      <Tooltip title='Reset all changes'>
                        <Button
                          size='small'
                          startIcon={<UndoIcon fontSize='small' />}
                          onClick={resetAll}
                          sx={{
                            ml: 1,
                            flexShrink: 0,
                            textTransform: 'none',
                            color: 'error.main',
                          }}
                        >
                          Reset
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {!hasInteracted ? (
                    <ManageShipmentItemsLandingState
                      searchQuery={searchQuery}
                      loading={loading}
                      onSearchChange={setSearchQuery}
                      onRefresh={() =>
                        runApiWithFilters({ query: searchQuery, activeFilters: filters })
                      }
                      isRefreshDisabled={
                        loading || (!searchQuery && !Object.values(filters).some(Boolean))
                      }
                    />
                  ) : (
                    <>
                      <Box
                        sx={{
                          px: 2,
                          pt: 1,
                          pb: 1,
                          bgcolor: 'background.paper',
                          flexShrink: 0,
                        }}
                      >
                        <ManageShipmentItemsFilterStatusBar />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <ManageShipmentItemsSearchBar
                            searchQuery={searchQuery}
                            loading={loading}
                            refreshTooltip='Refresh'
                            onSearchChange={setSearchQuery}
                            onRefresh={() =>
                              runApiWithFilters({ query: searchQuery, activeFilters: filters })
                            }
                            isRefreshDisabled={
                              loading || (!searchQuery && !Object.values(filters).some(Boolean))
                            }
                          />
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        {enabled &&
                          pinnedShipments.map((pinnedShipment) => (
                            <Box key={pinnedShipment.shipmentId} sx={{ mb: 1.5 }}>
                              <TargetShipmentAccordion
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...buildAccordionProps(pinnedShipment)}
                                pinnedRole={pinnedRole}
                                expanded={isPinnedExpanded(pinnedShipment)}
                                onToggle={() => onPinnedToggle(pinnedShipment)}
                              />
                            </Box>
                          ))}
                        {(loading || searchLoading) && <ManageShipmentItemsShipmentSkeleton />}
                        {!loading &&
                          !searchLoading &&
                          isEmpty(otherShipments) &&
                          searchResults === null &&
                          pinnedShipments.length === 0 && (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                minHeight: 340,
                                width: '100%',
                              }}
                            >
                              <Box
                                sx={{
                                  textAlign: 'center',
                                  width: '100%',
                                  maxWidth: 320,
                                  mx: 'auto',
                                }}
                              >
                                <Box sx={{ display: 'inline-flex', mb: 2.5 }}>
                                  <Box
                                    sx={{
                                      width: 110,
                                      height: 110,
                                      borderRadius: '50%',
                                      bgcolor: 'grey.100',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      '@keyframes truckDriveIn': {
                                        '0%': { transform: 'translateX(-60px)', opacity: 0 },
                                        '60%': { transform: 'translateX(6px)', opacity: 1 },
                                        '100%': { transform: 'translateX(0)', opacity: 1 },
                                      },
                                      '& .MuiSvgIcon-root': {
                                        animation: 'truckDriveIn 0.6s ease-out forwards',
                                      },
                                    }}
                                  >
                                    <LocalShippingOutlinedIcon
                                      sx={{ fontSize: 58, color: 'grey.400' }}
                                    />
                                  </Box>
                                </Box>
                                <Typography
                                  variant='subtitle1'
                                  fontWeight={700}
                                  color='text.primary'
                                  sx={{ mb: 0.75, fontSize: 15 }}
                                >
                                  No shipment selected
                                </Typography>
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                  sx={{ lineHeight: 1.6 }}
                                >
                                  Search or use filters to find a shipment, then select it to start
                                  moving items.
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        {!loading &&
                          !searchLoading &&
                          isEmpty(displayedShipments) &&
                          pinnedShipments.length === 0 &&
                          (searchResults !== null || !isEmpty(otherShipments)) && (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 360,
                                animation: `${fadeSlideUp} 0.4s ease-out both`,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 84,
                                  height: 84,
                                  borderRadius: 3,
                                  background: 'linear-gradient(145deg, #1e2a4a 0%, #2d3f6e 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mb: 3,
                                  boxShadow: '0 6px 24px rgba(30,42,74,0.28)',
                                }}
                              >
                                <SearchIcon
                                  sx={{
                                    fontSize: 40,
                                    color: '#fff',
                                    animation: `${scanAnim} 2.4s ease-in-out infinite`,
                                  }}
                                />
                              </Box>
                              <Typography
                                variant='h6'
                                fontWeight={700}
                                color='text.primary'
                                sx={{ mb: 1 }}
                              >
                                Nothing matched your search.
                              </Typography>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ textAlign: 'center', maxWidth: 250, lineHeight: 1.8 }}
                              >
                                Adjust the order ID or filters and try again.
                              </Typography>
                            </Box>
                          )}
                        {!loading &&
                          !searchLoading &&
                          enabled &&
                          map(displayedShipments, (targetShipment) => (
                            <TargetShipmentAccordion
                              key={targetShipment.shipmentId}
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {...buildAccordionProps(targetShipment)}
                              expanded={
                                isDraggingFromLeft || expandedTargetId === targetShipment.shipmentId
                              }
                              onToggle={() => {
                                const { shipmentId } = targetShipment;
                                setExpandedTargetId((prev) =>
                                  prev === shipmentId ? null : shipmentId
                                );
                              }}
                            />
                          ))}
                      </Box>
                      {paginationBar}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </DragDropContext>
        </DialogContent>
        <Divider />
        <ManageShipmentItemsFooter
          totalItemCount={totalItemCount}
          totalQty={totalQty}
          hasChanges={hasChanges}
          saving={saving}
          onSave={() => setConfirmOpen(true)}
        />
        <ManageShipmentItemsConfirmDialog
          open={confirmOpen}
          saving={saving}
          onClose={() => setConfirmOpen(false)}
          onConfirm={onSave}
          droppedItems={droppedItems}
          pickedItems={pickedItems}
          shipment={shipment}
          resolveShipmentData={(id) =>
            resolveShipment({ shipmentId: id, otherShipments, searchResults })
          }
        />
      </Dialog>
    </ManageShipmentItemsContext.Provider>
  );
};

ManageShipmentItemsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  shipment: PropTypes.shape({
    shipmentId: PropTypes.string.isRequired,
    referenceId: PropTypes.string,
    shipmentItems: PropTypes.arrayOf(PropTypes.shape({ lineItemId: PropTypes.string })),
    shipmentAddress: PropTypes.shape({
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
    fulfillingPharmacyId: PropTypes.string,
    partnerId: PropTypes.string,
    partnerName: PropTypes.string,
    practiceId: PropTypes.string,
    practiceName: PropTypes.string,
    recipientName: PropTypes.string,
  }).isRequired,
  fulfillingPharmacyName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onAllItemsMoved: PropTypes.func,
};

ManageShipmentItemsDialog.defaultProps = {
  fulfillingPharmacyName: '',
  onAllItemsMoved: null,
};

export default ManageShipmentItemsDialog;
